import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { getProducts, createProduct, updateProduct, deleteProduct } from "@/lib/api";
import { fmtEur } from "@/lib/format";
import { useI18n } from "@/i18n";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const empty = { name: "", description: "", unit_price: 0, tax_rate: 19, unit: "Stück" };

const Products = () => {
  const { t } = useI18n();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = () => getProducts().then(setList);
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditing(null); setOpen(true); };
  const openEdit = (p) => { setForm(p); setEditing(p.id); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error(t("common.required")); return; }
    const payload = {
      name: form.name, description: form.description, unit: form.unit,
      unit_price: Number(form.unit_price) || 0, tax_rate: Number(form.tax_rate) || 0,
    };
    if (editing) { await updateProduct(editing, payload); toast.success(t("common.save")); }
    else { await createProduct(payload); toast.success(t("common.create")); }
    setOpen(false); load();
  };

  const confirmDelete = async () => { await deleteProduct(toDelete.id); setToDelete(null); load(); toast.success(t("common.delete")); };

  return (
    <div>
      <PageHeader title={t("product.title")}>
        <Button data-testid="new-product-button" onClick={openNew} className="rounded-lg"><Plus className="mr-1.5 h-4 w-4" /> {t("product.new")}</Button>
      </PageHeader>

      {list.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground">{t("product.empty")}</Card>
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>{t("product.name")}</TableHead>
                <TableHead className="hidden md:table-cell">{t("product.unit")}</TableHead>
                <TableHead className="text-right">{t("product.taxRate")}</TableHead>
                <TableHead className="text-right">{t("product.unitPrice")}</TableHead>
                <TableHead className="w-24 text-right">{t("common.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((p, i) => (
                <motion.tr key={p.id} data-testid={`product-row-${p.id}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }} className="border-b transition-colors hover:bg-slate-50">
                  <TableCell>
                    <p className="font-medium text-slate-900">{p.name}</p>
                    {p.description && <p className="text-xs text-muted-foreground">{p.description}</p>}
                  </TableCell>
                  <TableCell className="hidden text-slate-600 md:table-cell">{p.unit}</TableCell>
                  <TableCell className="text-right font-mono-num text-slate-600">{p.tax_rate}%</TableCell>
                  <TableCell className="text-right font-mono-num font-semibold text-slate-900">{fmtEur(p.unit_price)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <button data-testid={`edit-product-${p.id}`} onClick={() => openEdit(p)} className="rounded-md p-1.5 text-slate-500 hover:bg-accent hover:text-primary"><Pencil className="h-4 w-4" /></button>
                      <button data-testid={`delete-product-${p.id}`} onClick={() => setToDelete(p)} className="rounded-md p-1.5 text-slate-500 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </TableCell>
                </motion.tr>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-xl">{editing ? t("product.edit") : t("product.new")}</DialogTitle></DialogHeader>
          <div className="grid gap-4">
            <div className="grid gap-1.5"><Label className="text-xs font-medium text-slate-600">{t("product.name")}</Label><Input data-testid="product-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="grid gap-1.5"><Label className="text-xs font-medium text-slate-600">{t("product.description")}</Label><Textarea data-testid="product-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid grid-cols-3 gap-4">
              <div className="grid gap-1.5"><Label className="text-xs font-medium text-slate-600">{t("product.unitPrice")}</Label><Input data-testid="product-price-input" type="number" step="0.01" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} /></div>
              <div className="grid gap-1.5"><Label className="text-xs font-medium text-slate-600">{t("product.taxRate")}</Label><Input data-testid="product-tax-input" type="number" step="0.1" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} /></div>
              <div className="grid gap-1.5"><Label className="text-xs font-medium text-slate-600">{t("product.unit")}</Label><Input data-testid="product-unit-input" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={save} data-testid="save-product-button">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>{t("common.delete")}</AlertDialogTitle><AlertDialogDescription>{t("common.deleteConfirm")}</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-product" className="bg-rose-600 hover:bg-rose-700">{t("common.delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Products;
